import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { take } from 'rxjs/operators';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Setup } from '../shared/service/setup';
import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PICKLIST_GROUPS } from '../../../core/config/picklist.config';

@Component({
  selector: 'app-city',
  standalone: true,
  imports: [TranslatePipe, CommonModule, Table, Popup],
  templateUrl: './city.html',
  styleUrl: './city.css',
})
export class City implements OnInit {

  cities: any[] = [];
  columns: TableColumn[] = [];
  clientFormFields: DynamicFieldConfig[] = [];

  showPopup = false;
  isEditMode = false;
  formData: any = {};

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklistService: PickListService
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.loadCities()
    this.loadPicklists();
  }

  // ================= PICKLIST =================
  loadPicklists(): void {
    this.picklistService.loadCodes([PICKLIST_GROUPS.STATUS]);

    this.picklistService.picklists$
      .pipe(take(1))
      .subscribe(() => {

        this.clientFormFields = [
          {
            key: 'citySeq',
            label: 'SETUP.ID',
            type: 'text',
            hidden: true,
          },
          {
            key: 'cityNm',
            label: 'SETUP.CITY.CITY_NAME',
            type: 'text',
            validations: { required: true },
          },
          {
            key: 'cityDscr',
            label: 'COMMON.DESCRIPTION',
            type: 'textarea',
          },
          {
            key: 'cityStsKey',
            label: 'COMMON.STATUS',
            type: 'select',
            options$: this.picklistService.getOptionsSync(PICKLIST_GROUPS.STATUS),
            validations: { required: true },
          },
        ];

        this.buildColumns();
        this.loadCities();
      });
  }

  // ================= TABLE =================
  buildColumns() {
    const statusOptions = this.picklistService.getOptionsSync(PICKLIST_GROUPS.STATUS);
    const statusMap: Record<string, string> = {};
    const statusBadgeMap: Record<string, string> = {};

    statusOptions.forEach((opt) => {
      statusMap[opt.value] = opt.label;
      statusBadgeMap[opt.value] =
        opt.label?.toLowerCase() === 'active' ? 'status-active' : 'status-inactive';
    });

    this.columns = [
      { key: 'cityCd', label: 'COMMON.CODE' },
      { key: 'cityNm', label: 'SETUP.CITY.CITY_NAME' },
      { key: 'cityDscr', label: 'COMMON.DESCRIPTION' },
      {
        key: 'cityStsKey',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: statusMap,
        badgeMap: statusBadgeMap
      }
    ];
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: 'images/edit-icon.png',
      tooltip: 'COMMON.EDIT',
      class: 'flex items-center justify-center',
      iconSize: 'md',
      onClick: (row: any) => this.openPopup(row),
    },
    {
      icon: 'images/del-icon.png',
      tooltip: 'COMMON.DELETE',
      class: 'flex items-center justify-center',
      iconSize: 'md',
      onClick: (row: any) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.cityNm });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.deleteCity(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= POPUP =================
  openPopup(data?: any) {
    this.showPopup = true;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        citySeq: data.citySeq,
        cityNm: data.cityNm,
        cityDscr: data.cityDscr,
        cityStsKey: data.cityStsKey   // ✅ direct
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        citySeq: null,
        cityNm: '',
        cityDscr: '',
        cityStsKey: this.getDefaultStatus(),
        delFlg: 0,
        tntKey: 0,
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklistService.getOptionsSync(PICKLIST_GROUPS.STATUS);
    return options?.[0]?.value || 706;
  }

  // ================= SAVE =================
  handleSave = (formValue: any) => {

    const payload = {
      citySeq: this.isEditMode ? formValue.citySeq : null,
      cityNm: formValue.cityNm,
      cityDscr: formValue.cityDscr,
      cityStsKey: formValue.cityStsKey,   // ✅ direct
      delFlg: 0,
      tntKey: 0,
    };

    return this.setup.saveCity(payload).subscribe({
      next: (res: any) => {

        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.UPDATED' : 'SETUP.CREATED'));
        this.showPopup = false;
        this.loadCities();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };

  // ================= DELETE =================
  deleteCity(row: any) {

    const payload = {
      citySeq: row.citySeq,
      cityNm: row.cityNm,
      cityDscr: row.cityDscr,
      cityStsKey: row.cityStsKey,
      delFlg: 1,
      tntKey: 0,
    };

    this.setup.saveCity(payload).subscribe({
      next: (res: any) => {

        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('SETUP.DELETED'));

        // 🔥 force clean UI refresh
        this.cities = [];
        this.loadCities();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED')),
    });
  }

  // ================= LOAD =================
  loadCities() {
    this.setup.getCity().subscribe({
      next: (res: any) => {

        // ✅ handle backend error object
        if (!res || res.error) {
          this.cities = [];
          return;
        }

        const data = Array.isArray(res) ? res : [];

        this.cities = data.map((item: any) => ({
          citySeq: item.citySeq,
          cityCd: item.cityCd || item.citySeq,
          cityNm: item.cityNm,
          cityDscr: item.cityDscr,
          cityStsKey: item.cityStsKey   // ✅ direct
        }));
      },
      error: () => {
        this.cities = [];
      },
    });
  }
}